import { supabase } from '@/lib/supabase'

// ── GroupDetail.jsx ──

export async function fetchGroupDetail({ groupId }) {
  const { data, error } = await supabase.rpc('get_group_detail', {
    p_group_id: groupId
  })
  if (error) throw error
  return data
}

export async function fetchGroupMembers({ groupId, limit = 50 }) {
  const { data, error } = await supabase.rpc('get_group_members', {
    p_group_id: groupId,
    p_limit: limit
  })
  if (error) throw error
  return data
}

export async function fetchGroupLeaderboard({ groupId, limit = 10 }) {
  const { data, error } = await supabase.rpc('get_leaderboard_rankings', {
    p_group_id: groupId,
    p_limit: limit
  })
  if (error) throw error
  return data
}

export async function fetchGroupActivity({ groupId, limit = 20 }) {
  const { data, error } = await supabase.rpc('get_group_activity', {
    p_group_id: groupId,
    p_limit: limit
  })
  if (error) throw error
  return data
}

export async function fetchGroupBrackets({ groupId, userId }) {
  const { data, error } = await supabase.rpc('get_group_brackets', {
    p_group_id: groupId,
    p_user_id: userId || null
  })
  if (error) throw error
  return data
}

export async function joinGroup({ groupId }) {
  const { data, error } = await supabase.rpc('join_group', {
    p_group_id: groupId
  })
  if (error) throw error
  return data
}

export async function leaveGroup({ groupId }) {
  const { data, error } = await supabase.rpc('leave_group', {
    p_group_id: groupId
  })
  if (error) throw error
  return data
}

export async function removeGroupMember({ groupId, userId }) {
  const { data, error } = await supabase.rpc('remove_group_member', {
    p_group_id: groupId,
    p_user_id: userId
  })
  if (error) throw error
  return data
}

// ── Groups.jsx ──

export async function fetchGroups({ userId, includePublic, searchTerm }) {
  const params = {}
  if (userId) params.p_user_id = userId
  if (includePublic) params.p_include_public = true
  if (searchTerm) params.p_search_term = searchTerm
  const { data, error } = await supabase.rpc('get_groups', params)
  if (error) throw error
  return data
}

export async function createGroup({ name, description, isPublic = true, isPrivate = false, groupType = null }) {
  const { data, error } = await supabase.rpc('create_group', {
    p_name: name,
    p_description: description,
    p_is_public: isPublic,
    p_private: isPrivate,
    p_group_type: groupType
  })
  if (error) throw error
  return data
}

// ── GroupSettings.jsx ──

export async function updateGroup({ groupId, name, description, isPublic, isPrivate }) {
  const { data, error } = await supabase.rpc('update_group', {
    p_group_id: groupId,
    p_name: name,
    p_description: description,
    p_is_public: isPublic,
    p_private: isPrivate
  })
  if (error) throw error
  return data
}

export async function deleteGroup({ groupId }) {
  const { data, error } = await supabase.rpc('delete_group', {
    p_group_id: groupId
  })
  if (error) throw error
  return data
}

export async function fetchAllGroupsAdmin({ searchTerm } = {}) {
  const { data, error } = await supabase.rpc('get_all_groups_admin', {
    p_search_term: searchTerm || null,
  })
  if (error) throw error
  return data
}

export async function transferGroupOwnership({ groupId, newOwnerId }) {
  const { data, error } = await supabase.rpc('transfer_group_ownership', {
    p_group_id: groupId,
    p_new_owner_id: newOwnerId
  })
  if (error) throw error
  return data
}

// ── GroupPrizes.jsx ──

export async function fetchGroupPrizes({ groupId }) {
  const { data, error } = await supabase.rpc('get_group_prizes', {
    p_group_id: groupId
  })
  if (error) throw error
  return data
}

export async function upsertPrize({ id, tournamentId, bracketId, groupId, name, description, place, promoCode, redeemUrl, combinedRedeemUrl }) {
  const { data, error } = await supabase.rpc('upsert_prize', {
    p_id: id || null,
    p_tournament_id: tournamentId,
    p_bracket_id: bracketId || null,
    p_group_id: groupId,
    p_name: name,
    p_description: description || null,
    p_place: place,
    p_promo_code: promoCode || null,
    p_redeem_url: redeemUrl || null,
    p_combined_redeem_url: combinedRedeemUrl || null,
  })
  if (error) throw error
  return data
}

export async function deletePrize({ prizeId }) {
  const { data, error } = await supabase.rpc('delete_prize', {
    p_prize_id: prizeId
  })
  if (error) throw error
  return data
}

export async function searchGroupUsers({ query, groupId, limit = 10 }) {
  const { data, error } = await supabase.rpc('search_users', {
    p_query: query,
    p_group_id: groupId,
    p_limit: limit
  })
  if (error) throw error
  return data
}

// Replaces the prize's winner set. Several ids = a tie; [] clears it.
export async function assignPrizeWinners({ prizeId, winnerUserIds }) {
  const { data, error } = await supabase.rpc('assign_prize_winners', {
    p_prize_id: prizeId,
    p_winner_user_ids: winnerUserIds || [],
  })
  if (error) throw error
  return data
}

export async function removeBracketFromGroup({ groupId, bracketId }) {
  const { data, error } = await supabase.rpc('remove_bracket_from_group', {
    p_group_id: groupId,
    p_bracket_id: bracketId
  })
  if (error) throw error
  return data
}

export async function removeTournamentFromGroup({ groupId, tournamentId }) {
  const { data, error } = await supabase.rpc('remove_tournament_from_group', {
    p_group_id: groupId,
    p_tournament_id: tournamentId
  })
  if (error) throw error
  return data
}

export async function fetchGroupAnnouncements({ groupId, limit = 20 }) {
  const { data, error } = await supabase.rpc('get_group_announcements', {
    p_group_id: groupId,
    p_limit: limit
  })
  if (error) throw error
  return data
}

// ── JoinGroup.jsx ──

// Callable signed out: the /join/:id page shows this before login.
export async function fetchGroupInvitePreview({ groupId }) {
  const { data, error } = await supabase.rpc('get_group_invite_preview', {
    p_group_id: groupId
  })
  if (error) throw error
  return data
}

// ── Phase 3 extras ──

export async function sendGroupKudos({ groupId, recipientId, kudosKey }) {
  const { data, error } = await supabase.rpc('send_group_kudos', {
    p_group_id: groupId,
    p_recipient_id: recipientId,
    p_kudos_key: kudosKey
  })
  if (error) throw error
  return data
}

// ── Messages feed: per-user "Hide" ──

// The caller's hidden feed items in this group: [{ item_kind, item_id }].
export async function fetchGroupMessageHides({ groupId }) {
  const { data, error } = await supabase.rpc('get_group_message_hides', {
    p_group_id: groupId
  })
  if (error) throw error
  return data
}

export async function setGroupMessageHidden({ groupId, kind, itemId, hidden }) {
  const { error } = await supabase.rpc('set_group_message_hidden', {
    p_group_id: groupId,
    p_item_kind: kind,
    p_item_id: String(itemId),
    p_hidden: !!hidden
  })
  if (error) throw error
}

export async function relaunchGroup({ groupId }) {
  const { data, error } = await supabase.rpc('relaunch_group', {
    p_group_id: groupId
  })
  if (error) throw error
  return data
}

export async function setGroupEmailOptOut({ groupId, optOut }) {
  const { data, error } = await supabase.rpc('set_group_email_optout', {
    p_group_id: groupId,
    p_opt_out: optOut
  })
  if (error) throw error
  return data
}

// Emails an announcement the owner just posted (group-email Edge Function).
export async function emailGroupAnnouncement({ announcementId }) {
  const { data, error } = await supabase.functions.invoke('group-email', {
    body: { announcementId }
  })
  if (error) {
    // Supabase wraps non-2xx responses; surface the function's own message.
    let parsed = null
    try {
      parsed = await error.context?.json()
    } catch { /* not JSON */ }
    throw new Error(parsed?.error || error.message || 'The email could not be sent')
  }
  return data
}
